import type { ColumnsType } from 'antd/es/table';
import { IoBookmarkSharp } from 'react-icons/io5';

import VerseNote from './VerseNote';
import type { GetTableColumnsParams, VerseRowRecord, IVersesRow } from './types';
import { hasNoteForVerse } from './utils';

export function getTableColumns({
  readingData,
  selectedVerses,
  translationIds,
  onCellSelection,
  onBookmarkClick,
  isVerseBookmarked,
  notesConfig,
  notesIndicatorConfig,
  isUser,
}: GetTableColumnsParams): ColumnsType<VerseRowRecord> {
  const showBookmark = Boolean(isUser);
  const columnCount = readingData.chapters.length + (notesConfig ? 1 : 0);
  const columnWidth = `${100 / columnCount}%`;

  const translationColumns = readingData.chapters.map((chapterData, columnIndex) => {
    const translationId = translationIds[columnIndex] ?? 0;
    const chapterTitle = translationId === 6 ? '' : `${chapterData.number}. ${chapterData.title}`;

    return {
      title: chapterTitle,
      dataIndex: `translation${columnIndex}`,
      width: columnWidth,
      onCell: (record: VerseRowRecord) => {
        const showNoteDot = notesIndicatorConfig
          && hasNoteForVerse(
            notesIndicatorConfig.notes,
            notesIndicatorConfig.bookNumber,
            notesIndicatorConfig.chapterNumber,
            record.verse,
          );
        return showNoteDot
          ? { className: 'bible-v2-viewer-content__td-has-note', style: { position: 'relative' as const } }
          : {};
      },
      render: (cell: IVersesRow, record: VerseRowRecord) => {
        const isSelected = showBookmark && selectedVerses.some(
          v => v.verseNumber === record.verse && v.translationId === translationId,
        );
        const isBookmarked = showBookmark && isVerseBookmarked(record.verse, translationId);
        const displayText = cell?.text ?? '';

        return (
          <div
            role={showBookmark ? 'button' : undefined}
            className={`
              bible-v2-viewer-content__cell
              ${isSelected ? 'bible-v2-viewer-content__cell--selected' : ''}
              ${cell?.is_start_of_paragraph === '1' ? 'bible-v2-viewer-content__cell--paragraph-start' : ''}
            `}
            onClick={showBookmark ? () => onCellSelection(record.verse, columnIndex, displayText) : undefined}
          >
            {showBookmark && (
              <IoBookmarkSharp
                className={`
                  bible-v2-viewer-content__cell-bookmark
                  ${isSelected ? 'bible-v2-viewer-content__cell-bookmark--selected' : ''}
                  ${isBookmarked ? 'bible-v2-viewer-content__cell-bookmark--bookmarked' : ''}
                `}
                onClick={e => {
                  e.stopPropagation();
                  onBookmarkClick(record.verse, columnIndex);
                }}
              />
            )}
            {cell?.title && <div className="bible-v2-viewer-content__verse-title">{cell.title}</div>}
            {cell?.cross_reference && (
              <div className="bible-v2-viewer-content__cross-ref">{cell.cross_reference}</div>
            )}
            {record.verse > 0 && (
              <span className="bible-v2-viewer-content__verse-num">
                {record.verse}
                {' '}
              </span>
            )}
            {displayText}
          </div>
        );
      },
    };
  });

  const notesColumn = notesConfig
    ? {
      title: notesConfig.notesColumnTitle,
      dataIndex: 'verse',
      width: columnWidth,
      render: (_: unknown, record: VerseRowRecord) => {
        const note = notesConfig.notes.find(
          n => n.verseIndex === record.verse
              && n.bookNumber === notesConfig.bookNumber
              && n.chapterNumber === notesConfig.chapterNumber,
        );
        return (
          <VerseNote
            note={note ?? { verseIndex: record.verse, text: '', bookNumber: notesConfig.bookNumber, chapterNumber: notesConfig.chapterNumber }}
            bookNumber={notesConfig.bookNumber}
            chapterNumber={notesConfig.chapterNumber}
            verseIndex={record.verse}
          />
        );
      },
    }
    : null;

  return notesColumn ? [...translationColumns, notesColumn] : translationColumns;
}
